/**
 * Scheduled-dashboard KPIs (live firm vs forecast).
 *
 * Backed by ``analytics/views.scheduled_dashboard`` — splits forward-dated PO
 * line schedules into a "firm" window (within each part's schedule_firm days)
 * and "forecast" (beyond it). Tenant default firm window applies when a part
 * has no PartScheduleEntry.
 */
import api, { type AxiosResponse } from './client';

export interface ScheduledBucket {
  schedules: number;
  total_qty: number;
  open_qty: number;
  shipped_qty: number;
  total_value: number;
  open_value: number;
}

export interface QualityMetrics {
  ncrs: number;
  reject_qty: number;
  received_qty: number;
  ppm: number;
}

export interface DeliveryMetrics {
  // Schedule-line based OTIF/OTD (canonical).
  // OTIF = schedule lines fulfilled in-full within the part's firm window.
  // OTD  = schedule lines whose deliveries arrived within the firm window.
  otif_pct: number;
  otd_pct: number;
  total_due: number;
  otif_met: number;
  otd_met: number;
  in_full: number;
  not_delivered: number;
  // OTIF Mix (3-state): collapsed Passed / Pending / Failed buckets used by
  // the donut. Pending excludes any schedule still inside its late-tolerance
  // window AND not yet delivered; Failed includes both "delivered but didn't
  // pass OTIF" and "past window with no delivery".
  passed: number;
  pending: number;
  failed: number;
  // GRN-status counters — used for the OTD-mix donut breakdown only.
  on_time: number;
  early: number;
  late: number;
  total_with_status: number;
  total_grns: number;
  appeals: number;
}

export interface CostMetrics {
  // Period-bound — driven by schedule lines whose schedule_date is in window.
  total_lines: number;        // committed lines this period
  open_lines: number;         // still outstanding
  total_value: number;        // schedule_qty × unit_price
  received_value: number;     // shipped_qty × unit_price (delivered against)
  open_value: number;         // balance_qty × unit_price (still owed)
  arrears_lines: number;      // past-due AND still open within the period
  arrears_value: number;
  spend: number;              // legacy GRN-receipt-window spend (per-week chart)
}

export interface DeliveryByDay {
  day: 'Sun' | 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat';
  on_time: number;
  early: number;
  late: number;
  pending: number;
}

export interface PeriodData {
  window_start: string;
  window_end: string;
  quality: QualityMetrics;
  delivery: DeliveryMetrics;
  cost: CostMetrics;
}

export interface CurrentWeekData extends PeriodData {
  otif_window_start: string;
  otif_window_end: string;
  frozen: boolean;
  deliveries_by_day: DeliveryByDay[];
}

export interface WeekTrendPoint extends PeriodData {
  label: string;
  week_start: string;
  week_end: string;
}

export interface MonthTrendPoint extends PeriodData {
  label: string;
  month_start: string;
  month_end: string;
}

export interface PortfolioContext {
  // Point-in-time aggregates over forward (or past-due) real schedules.
  // These respect the active filter scope (customer, supplier, erp_order_type,
  // part_number) — they are NOT a global cross-tenant figure.
  open_orders_value: number;
  arrears_value: number;
}

export interface ScheduledDashboardData {
  customer_id: number;
  supplier_id: number | null;
  default_firm_days: number;
  // Echoed back so the UI can reflect the active scope.
  erp_order_type: string | null;
  part_number: string | null;
  firm: ScheduledBucket;
  forecast: ScheduledBucket;
  today: string;
  // Customer-level setting that gates OTIF / OTD blocks across all
  // dashboard surfaces. Mirrors PerformanceSettings.delivery_metric_mode.
  delivery_metric_mode?: 'both' | 'otif_only' | 'otd_only';
  portfolio: PortfolioContext;
  current_week: CurrentWeekData;
  ytd: PeriodData;
  per_week: { weeks: WeekTrendPoint[] };
  per_month: { months: MonthTrendPoint[] };
}

export interface ScheduledDashboardParams {
  customer_id?: number;
  supplier_id?: number;
  default_firm_days?: number;
  // '4' = scheduling agreement (Scheduled Dashboards tab)
  // '1' = spot order (Spot Orders tab)
  erp_order_type?: string;
  part_number?: string;
}

export const scheduledDashboardAPI = {
  get(params?: ScheduledDashboardParams): Promise<AxiosResponse<ScheduledDashboardData>> {
    return api.get('/analytics/scheduled-dashboard/', { params });
  },
};
